import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Plus, Minus, Trash2 } from "lucide-react";
import { getUserFriendlyError } from "@/lib/errors";

const checkoutFormSchema = z.object({
  name: z.string().min(1, "Naam is verplicht"),
  email: z.string().email("Ongeldig e-mailadres"),
  phone: z.string().optional(),
  street: z.string().min(1, "Straatnaam is verplicht"),
  city: z.string().min(1, "Plaats is verplicht"),
  postalCode: z.string().min(1, "Postcode is verplicht"),
  acceptTerms: z.boolean().refine((val) => val === true, "Je moet akkoord gaan met de voorwaarden"),
});

interface CartItem {
  id: string;
  quantity: number;
  product: {
    id: string;
    name: string;
    description: string;
    price: number;
    image_url: string;
    free_shipping?: boolean;
    stripe_price_id?: string | null;
  };
}

const Cart = () => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const form = useForm<z.infer<typeof checkoutFormSchema>>({
    resolver: zodResolver(checkoutFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      street: "",
      city: "",
      postalCode: "",
      acceptTerms: false,
    },
  });

  useEffect(() => {
    fetchCart();
  }, []);

  const fetchCart = () => {
    try {
      const cart = JSON.parse(localStorage.getItem("cart") || "[]");
      setCartItems(cart);
    } catch (error: unknown) {
      toast({
        title: "Fout bij laden winkelwagen",
        description: getUserFriendlyError(error, "Winkelwagen laden"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = (itemId: string, newQuantity: number) => {
    if (newQuantity < 1) return;

    try {
      const cart = JSON.parse(localStorage.getItem("cart") || "[]");
      const itemIndex = cart.findIndex((item: any) => item.id === itemId);
      
      if (itemIndex >= 0) {
        cart[itemIndex].quantity = newQuantity;
        localStorage.setItem("cart", JSON.stringify(cart));
        window.dispatchEvent(new Event('cartUpdated'));
        fetchCart();
      }
    } catch (error: unknown) {
      toast({
        title: "Fout",
        description: getUserFriendlyError(error, "Hoeveelheid wijzigen"),
        variant: "destructive",
      });
    }
  };

  const removeItem = (itemId: string) => {
    try {
      const cart = JSON.parse(localStorage.getItem("cart") || "[]");
      const updatedCart = cart.filter((item: any) => item.id !== itemId);
      localStorage.setItem("cart", JSON.stringify(updatedCart));
      window.dispatchEvent(new Event('cartUpdated'));
      toast({ title: "Product verwijderd uit winkelwagen" });
      fetchCart();
    } catch (error: unknown) {
      toast({
        title: "Fout",
        description: getUserFriendlyError(error, "Product verwijderen"),
        variant: "destructive",
      });
    }
  };

  const handleCheckout = async (values: z.infer<typeof checkoutFormSchema>) => {
    setCheckingOut(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { 
          items: cartItems,
          customerEmail: values.email,
          total: total,
          customerInfo: {
            name: values.name,
            phone: values.phone,
            street: values.street,
            city: values.city,
            postalCode: values.postalCode,
          },
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error: unknown) {
      toast({
        title: "Checkout fout",
        description: getUserFriendlyError(error, "Checkout"),
        variant: "destructive",
      });
    } finally {
      setCheckingOut(false);
    }
  };

  const total = cartItems.reduce(
    (sum, item) => sum + item.product.price * item.quantity,
    0
  );

  // Check if any item in the cart has free shipping
  const hasFreeShippingProduct = cartItems.some(item => item.product.free_shipping === true);
  const shippingCost = hasFreeShippingProduct || total >= 50 ? 0 : 6.95;

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="container mx-auto px-6 py-32">
          <p className="text-center">Winkelwagen laden...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEO
        title="Winkelwagen | Bo Barbers"
        description="Bekijk je winkelwagen en reken veilig af. Bestellingen voor 15:00 op werkdagen worden dezelfde dag verzonden."
        path="/cart"
      />
      <Header />
      
      <section className="pt-24 sm:pt-28 md:pt-32 pb-16 sm:pb-24 md:pb-32 px-4 sm:px-6">
        <div className="container mx-auto max-w-4xl">
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-8 sm:mb-12 text-foreground">
            WINKELWAGEN
          </h1>

          {cartItems.length === 0 ? (
            <Card className="p-8 sm:p-12 text-center">
              <p className="text-foreground/60 mb-4 sm:mb-6 text-sm sm:text-base">Je winkelwagen is leeg</p>
              <Button onClick={() => navigate("/products")} className="w-full sm:w-auto">
                Verder winkelen
              </Button>
            </Card>
          ) : (
            <div className="space-y-4 sm:space-y-6">
              {cartItems.map((item) => (
                <Card key={item.id} className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <img
                      src={item.product.image_url}
                      alt={item.product.name}
                      className="w-full sm:w-24 h-48 sm:h-24 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h2 className="font-display text-base sm:text-lg font-bold mb-1">
                        {item.product.name}
                      </h2>
                      <p className="text-foreground/60 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">
                        {item.product.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="h-8 w-8 p-0"
                            aria-label="Verlaag aantal"
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="w-8 text-center text-sm">{item.quantity}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="h-8 w-8 p-0"
                            aria-label="Verhoog aantal"
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                        <span className="font-bold text-sm sm:text-base">
                          €{(item.product.price * item.quantity).toFixed(2)}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeItem(item.id)}
                          className="ml-auto"
                          aria-label="Verwijder product"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}

              <Card className="p-4 sm:p-6">
                <h2 className="font-display text-lg sm:text-xl font-bold mb-4 sm:mb-6">Jouw Gegevens</h2>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleCheckout)} className="space-y-3 sm:space-y-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm">Naam</FormLabel>
                          <FormControl>
                            <Input placeholder="Voor- en achternaam" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm">E-mailadres</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="je@email.nl" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm">Telefoonnummer (optioneel)</FormLabel>
                          <FormControl>
                            <Input type="tel" placeholder="06 12345678" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="street"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm">Straatnaam + huisnummer</FormLabel>
                          <FormControl>
                            <Input placeholder="Straat 123" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <FormField
                        control={form.control}
                        name="postalCode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm">Postcode</FormLabel>
                            <FormControl>
                              <Input placeholder="1234 AB" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm">Plaats</FormLabel>
                            <FormControl>
                              <Input placeholder="Amsterdam" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="acceptTerms"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel className="text-xs sm:text-sm">
                              Ik ga akkoord met de algemene voorwaarden en het retourbeleid
                            </FormLabel>
                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />
                    <div className="pt-3 sm:pt-4 border-t">
                      <div className="space-y-2 mb-3 sm:mb-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-foreground/60">Subtotaal</span>
                          <span>€{total.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-foreground/60">Verzendkosten</span>
                          <span>{shippingCost === 0 ? "Gratis" : "€6,95"}</span>
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t">
                          <span className="text-lg sm:text-xl font-bold">Totaal</span>
                          <span className="text-xl sm:text-2xl font-bold">
                            €{(total + shippingCost).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <Button
                        type="submit"
                        disabled={checkingOut}
                        className="w-full"
                        size="lg"
                      >
                        {checkingOut ? "Bezig..." : "Afrekenen met Stripe"}
                      </Button>
                    </div>
                  </form>
                </Form>
              </Card>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Cart;
